import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { getSystemSetting } from "@/app/actions/settings";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const FILE_SIGNATURES: Record<string, number[]> = {
  pdf: [0x25, 0x50, 0x44, 0x46],
  jpeg: [0xff, 0xd8, 0xff],
  png: [0x89, 0x50, 0x4e, 0x47],
  gif: [0x47, 0x49, 0x46],
};

export async function validateFileMagicBytes(file: File, expectedType: "pdf" | "jpeg" | "png" | "gif"): Promise<boolean> {
  const signature = FILE_SIGNATURES[expectedType];
  if (!signature) return false;

  const buffer = await file.slice(0, signature.length).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  return signature.every((byte, index) => bytes[index] === byte);
}

export function formatVND(amount: number): string {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(amount);
}

export function formatNumber(amount: number): string {
  return new Intl.NumberFormat("vi-VN").format(amount);
}

export function calculateVAT(amount: number, vatRate: number = 0.1): number {
  return amount * vatRate;
}

export function calculateTotal(subtotal: number, vatRate: number = 0.1): number {
  return subtotal + calculateVAT(subtotal, vatRate);
}

export function generateOrderId(
  orderType: "initial" | "upsell" | "renewal" = "initial",
  resellerShortname?: string | null
): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const shortname = resellerShortname?.trim() || "NO_SHORTNAME";
  const typeLabel =
    orderType === "upsell" ? "Upsell" : orderType === "renewal" ? "Renewal" : "New";
  return `SNS/${shortname}/DBX/${date}/${typeLabel}`;
}

/**
 * Generate a unique order ID by checking existing orders and appending sequence number if needed.
 * @param existingOrderNumbers - Array of existing order numbers from the database
 * @param orderType - Type of order (initial, upsell, renewal)
 * @param resellerShortname - Reseller's shortname
 * @returns Unique order number with sequence suffix if needed (e.g., SNS/ABC/DBX/20260421/New-01)
 */
export function generateUniqueOrderId(
  existingOrderNumbers: string[],
  orderType: "initial" | "upsell" | "renewal",
  resellerShortname?: string | null
): string {
  const baseOrderNumber = generateOrderId(orderType, resellerShortname);
  
  if (!existingOrderNumbers.includes(baseOrderNumber)) {
    return baseOrderNumber;
  }
  
  let sequence = 1;
  while (true) {
    const sequencedOrderNumber = `${baseOrderNumber}-${String(sequence).padStart(2, "0")}`;
    if (!existingOrderNumbers.includes(sequencedOrderNumber)) {
      return sequencedOrderNumber;
    }
    sequence++;
  }
}

export function generateShortnameFromCompanyName(companyName: string | null | undefined): string {
  if (!companyName?.trim()) return "";
  return companyName
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .substring(0, 10);
}

export function generateInvoiceId(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `INV-${timestamp}-${random}`;
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("vi-VN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat("vi-VN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(date));
}

export const ORDER_STATUS = {
  SUBMITTED: "submitted",
  APPROVED: "approved",
  DENIED: "denied",
  PAID: "paid",
  COMPLETED: "completed",
  PROVISIONING_FAILED: "provisioning_failed",
  CANCELLED: "cancelled",
} as const;

export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  submitted: "Submitted",
  approved: "Approved",
  denied: "Denied",
  paid: "Paid",
  completed: "Completed",
  provisioning_failed: "Provisioning Failed",
  cancelled: "Cancelled",
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  submitted: "bg-orange-100 text-orange-800",
  approved: "bg-green-100 text-green-800",
  denied: "bg-red-100 text-red-800",
  paid: "bg-emerald-100 text-emerald-800",
  completed: "bg-blue-100 text-blue-800",
  provisioning_failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-200 text-gray-700",
};

export const ROLES = {
  RESELLER: "reseller",
  SUB_ADMIN: "sub_admin",
  SUPER_ADMIN: "super_admin",
} as const;

export type UserRole = (typeof ROLES)[keyof typeof ROLES];

export const VAT_RATE = 0.1; // 10% VAT in Vietnam (default)

// Cache for VAT setting to avoid repeated DB calls
let vatEnabledCache: boolean | null = null;
let vatCacheTimestamp: number = 0;
const VAT_CACHE_TTL = 60000; // 1 minute cache

/**
 * Get VAT rate dynamically based on system settings
 * Uses cache to avoid repeated database calls
 */
export async function getVatRate(): Promise<number> {
  const now = Date.now();
  
  // Check if cache is valid
  if (vatEnabledCache !== null && (now - vatCacheTimestamp) < VAT_CACHE_TTL) {
    return vatEnabledCache ? VAT_RATE : 0;
  }
  
  // Fetch fresh value
  const vatSetting = await getSystemSetting("vat_enabled");
  const enabled = vatSetting === "true";
  
  // Update cache
  vatEnabledCache = enabled;
  vatCacheTimestamp = now;
  
  return enabled ? VAT_RATE : 0;
}

/**
 * Check if VAT is enabled in system settings
 */
export async function isVatEnabled(): Promise<boolean> {
  const vatSetting = await getSystemSetting("vat_enabled");
  return vatSetting === "true";
}

/**
 * Clear VAT cache (useful after settings change)
 */
export function clearVatCache(): void {
  vatEnabledCache = null;
  vatCacheTimestamp = 0;
}

export function getOrderStatusLabel(status: string): string {
  if (status === "paid") return "Paid"; // Backwards compatibility
  return ORDER_STATUS_LABELS[status as OrderStatus] || status;
}

export function getOrderStatusColor(status: string): string {
  if (status === "paid") return "bg-purple-100 text-purple-800"; // Backwards compatibility
  return ORDER_STATUS_COLORS[status as OrderStatus] || "bg-gray-100 text-gray-800";
}

/**
 * Calculate days until a given date
 * @param dateStr - ISO date string
 * @returns Number of days (negative if in the past)
 */
export function getDaysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

/**
 * True if a Supabase/Postgres write error is the unique-violation on
 * profiles.dropbox_reseller_id (see migration 037) — i.e. this Dropbox
 * Reseller ID is already claimed by a different profile.
 */
export function isDropboxResellerIdConflict(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return error.code === "23505" && (error.message?.includes("dropbox_reseller_id") ?? false);
}
